import {
  answerUploadTicketSchema,
  API_ROUTES,
  askedClientQuestionSchema,
  clientQuestionPreviewSchema,
  clientQuestionSchema,
  type AskClientQuestionRequest,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

export const questionKeys = {
  all: (organizationId: string) => ['questions', organizationId] as const,
};

export const questionsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: questionKeys.all(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.questions.list(organizationId),
        schema: z.array(clientQuestionSchema),
        signal,
      }),
    staleTime: 15_000,
  });

/** Resolves to the question and its link, shown once and never stored. */
export const askClientQuestion = (organizationId: string, body: AskClientQuestionRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.questions.list(organizationId),
    schema: askedClientQuestionSchema,
    body,
  });

export const withdrawClientQuestion = (organizationId: string, questionId: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.questions.withdraw(organizationId, questionId),
    schema: z.undefined(),
  });

/* ---- the client's side, no sign-in ---------------------------------------- */

export const questionPreviewQuery = (token: string) =>
  queryOptions({
    queryKey: ['question-link', token] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.questions.preview(token),
        schema: clientQuestionPreviewSchema,
        signal,
      }),
    retry: false,
    staleTime: 0,
  });

export const answerQuestion = (token: string, answerText: string, answeredName: string | null) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.questions.answer(token),
    schema: z.undefined(),
    body: { answerText, answeredName },
  });

export const requestAnswerUpload = (
  token: string,
  body: { filename: string; mimeType: string; byteSize: number },
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.questions.uploadTicket(token),
    schema: answerUploadTicketSchema,
    body,
  });

export const fileAnswer = (
  token: string,
  body: {
    objectKey: string;
    filename: string;
    mimeType: string;
    byteSize: number;
    sha256: string;
    answerText: string | null;
    answeredName: string | null;
  },
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.questions.file(token),
    schema: z.object({ documentId: z.string() }),
    body,
  });
